import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageCircle, X, Send, Bot, User, AlertTriangle, 
  Phone, Calendar, FileText, ExternalLink, Minimize2, Maximize2,
  Mic, MicOff, Volume2, VolumeX, Keyboard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AI Assistant Chat Widget
const AIAssistant = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [assistantStatus, setAssistantStatus] = useState(null);
  const [showDisclosure, setShowDisclosure] = useState(true);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check assistant status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/assistant/status`);
        if (response.ok) {
          const data = await response.json();
          setAssistantStatus(data);
        }
      } catch (error) {
        console.log('AI Assistant status check failed');
        setAssistantStatus({ enabled: false });
      }
    };
    
    if (isOpen) {
      checkStatus();
    }
  }, [isOpen]);

  // Add welcome message when opening
  useEffect(() => {
    if (isOpen && messages.length === 0 && !showDisclosure) {
      setMessages([{
        role: 'assistant',
        content: `Hello! I'm the Dysphagia Therapy Plus AI Assistant. I can help you with:

• **Services** - Learn about our therapy options
• **Insurance** - Understand coverage and billing
• **Scheduling** - Start the appointment process
• **Referrals** - Information for patients and providers

How can I help you today?`
      }]);
    }
  }, [isOpen, showDisclosure, messages.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: messages.slice(-6) // Send last 6 messages for context
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response,
          actions: data.suggested_actions,
          category: data.category,
          requiresEscalation: data.requires_escalation
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "I'm having trouble responding right now. Please try again or contact us directly at the phone number below.",
          isError: true
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble connecting. Please try again or contact us directly.",
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    const actionMessages = {
      'schedule_evaluation': "I'd like to schedule an evaluation",
      'contact_us': "How can I contact you?",
      'learn_more_services': "Tell me more about your services",
      'verify_insurance': "I have questions about insurance",
      'start_here': "I'm a new patient, where do I start?",
      'provider_referrals_page': "I'm a healthcare provider looking to refer a patient"
    };
    
    if (actionMessages[action]) {
      setInputValue(actionMessages[action]);
    }
  };

  const acceptDisclosure = () => {
    setShowDisclosure(false);
  };

  if (!isOpen) return null;

  // Disclosure screen
  if (showDisclosure) {
    return (
      <div className="fixed bottom-4 right-4 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
        <div className="bg-teal-700 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot size={24} />
              <span className="font-semibold">Dysphagia Therapy Plus AI Assistant</span>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-teal-600 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle size={24} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Before We Start</h3>
              <p className="text-sm text-slate-600 mb-3">
                This AI assistant provides <strong>general information about our practice and services</strong>. 
                It does not provide diagnosis or medical treatment.
              </p>
            </div>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-4 mb-4 text-sm">
            <p className="font-medium text-slate-800 mb-2">This AI assistant cannot:</p>
            <ul className="text-slate-600 space-y-1">
              <li>• Diagnose conditions</li>
              <li>• Provide medical treatment</li>
              <li>• Recommend medications</li>
              <li>• Access your medical records</li>
              <li>• Guarantee insurance coverage</li>
            </ul>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-amber-800">
              <strong>Privacy:</strong> For your privacy, please don't share sensitive medical information here. 
              I can help you continue through our secure patient intake or patient portal.
            </p>
          </div>
          
          <button
            onClick={acceptDisclosure}
            className="w-full btn-primary"
            data-testid="ai-accept-disclosure"
          >
            I Understand, Continue
          </button>
        </div>
      </div>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 bg-teal-700 text-white p-4 rounded-full shadow-lg hover:bg-teal-800 transition-colors z-50"
        aria-label="Open AI Assistant"
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-96 max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-teal-700 text-white p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot size={20} />
            <div>
              <span className="font-semibold text-sm">Dysphagia Therapy Plus</span>
              <span className="text-xs text-teal-200 block">AI Assistant</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setIsMinimized(true)}
              className="p-1.5 hover:bg-teal-600 rounded-lg transition-colors"
              aria-label="Minimize"
            >
              <Minimize2 size={16} />
            </button>
            <button 
              onClick={onClose}
              className="p-1.5 hover:bg-teal-600 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role === 'assistant' && (
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot size={16} className="text-teal-700" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-teal-700 text-white rounded-br-md'
                  : message.isError
                  ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md'
                  : message.requiresEscalation
                  ? 'bg-amber-50 text-amber-900 border border-amber-200 rounded-bl-md'
                  : 'bg-slate-100 text-slate-800 rounded-bl-md'
              }`}
            >
              <div className="text-sm whitespace-pre-wrap prose prose-sm max-w-none">
                {message.content.split('\n').map((line, i) => (
                  <p key={i} className="mb-1 last:mb-0">
                    {line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .split(/<strong>|<\/strong>/)
                      .map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
                  </p>
                ))}
              </div>
              
              {/* Quick action buttons */}
              {message.actions && message.actions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-200">
                  {message.actions.slice(0, 3).map((action, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuickAction(action)}
                      className="text-xs bg-white text-teal-700 px-3 py-1.5 rounded-full border border-teal-200 hover:bg-teal-50 transition-colors"
                    >
                      {action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {message.role === 'user' && (
              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                <User size={16} className="text-slate-600" />
              </div>
            )}
          </div>
        ))}
        
        {isLoading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
              <Bot size={16} className="text-teal-700" />
            </div>
            <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <div className="p-4 border-t border-slate-200 flex-shrink-0">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your question..."
            className="flex-1 px-4 py-2 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            disabled={isLoading}
            data-testid="ai-chat-input"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="p-2 bg-teal-700 text-white rounded-full hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Send message"
            data-testid="ai-chat-send"
          >
            <Send size={18} />
          </button>
        </form>
        
        <p className="text-xs text-slate-400 text-center mt-2">
          AI responses are for general information only
        </p>
      </div>
    </div>
  );
};

// Floating button to open assistant
export const AIAssistantButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="fixed bottom-4 right-4 bg-teal-700 text-white p-4 rounded-full shadow-lg hover:bg-teal-800 transition-all hover:scale-105 z-40 group"
      aria-label="Open AI Assistant"
      data-testid="ai-assistant-button"
    >
      <MessageCircle size={24} />
      <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
        Have questions?
      </span>
    </button>
  );
};

export default AIAssistant;
